import { AlertTriangle, Compass, Gauge, ShieldCheck } from 'lucide-react';
import React from 'react';

import type { SectorRecommendationItem, SectorTacticalAllocation } from '../types';
import { cn, formatConfidence, sectorName } from '../utils/formatters';

interface Props {
    tactical: SectorTacticalAllocation;
}

// Renders the fused macro/AFML posture that drives the whole page: the active
// regime, the rules-engine confidence, the AFML transition risk, and -- most
// importantly -- the "front-run" derisk alert that flips the recommendations
// into defensive sectors when transition_prob breaches the 0.70 threshold.
export const MacroContextBanner: React.FC<Props> = ({ tactical }) => {
    const { macro_context: ctx, recommendations: recs } = tactical;
    const isDerisk = ctx.defensive_overlay_active;

    return (
        <div
            className={cn(
                'rounded-xl border p-5 md:p-6 shadow-sm relative overflow-hidden',
                isDerisk
                    ? 'bg-rose-50/70 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800'
                    : 'bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-800',
            )}
        >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                {/* Left: regime identity + outlook */}
                <div className="flex items-start gap-4">
                    <div
                        className={cn(
                            'p-2.5 rounded-lg border',
                            isDerisk
                                ? 'bg-rose-100 text-rose-600 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800'
                                : 'bg-indigo-50 text-indigo-600 border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',
                        )}
                    >
                        {isDerisk ? (
                            <AlertTriangle className="w-6 h-6" />
                        ) : (
                            <Compass className="w-6 h-6" />
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                                {ctx.active_regime}
                            </h2>
                            <span
                                className={cn(
                                    'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border',
                                    isDerisk
                                        ? 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800'
                                        : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
                                )}
                            >
                                {isDerisk ? 'Defensive Overlay' : 'Stable Regime'}
                            </span>
                        </div>
                        <p className="text-sm mt-1 font-medium text-slate-600 dark:text-slate-300 max-w-xl">
                            {ctx.sector_outlook}
                        </p>
                    </div>
                </div>

                {/* Right: the two macro gauges */}
                <div className="flex items-stretch gap-3 shrink-0">
                    <Metric
                        icon={Gauge}
                        label="Macro Regime Confidence"
                        value={formatConfidence(ctx.regime_confidence)}
                        tone="neutral"
                    />
                    <Metric
                        icon={ShieldCheck}
                        label="AFML-Estimated Transition Risk"
                        value={formatConfidence(ctx.afml_transition_risk)}
                        tone={isDerisk ? 'danger' : 'neutral'}
                    />
                </div>
            </div>

            {/* Recommendation pills */}
            <div className="mt-5 pt-4 border-t border-slate-200/70 dark:border-slate-700/50 grid grid-cols-1 md:grid-cols-2 gap-4">
                <RecList
                    title="Overweight"
                    tone="positive"
                    items={recs.overweight}
                />
                <RecList
                    title="Underweight"
                    tone="negative"
                    items={recs.underweight}
                />
            </div>
        </div>
    );
};

const Metric: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string;
    tone: 'neutral' | 'danger';
}> = ({ icon: Icon, label, value, tone }) => (
    <div className="bg-white/70 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3 min-w-[140px]">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            <Icon className="w-3.5 h-3.5" />
            {label}
        </div>
        <div
            className={cn(
                'text-2xl font-bold tracking-tight mt-1',
                tone === 'danger'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-slate-900 dark:text-white',
            )}
        >
            {value}
        </div>
    </div>
);

const RecList: React.FC<{
    title: string;
    tone: 'positive' | 'negative';
    items: SectorRecommendationItem[];
}> = ({ title, tone, items }) => (
    <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
            {title}
        </div>
        <div className="flex flex-wrap gap-2">
            {items.length === 0 && (
                <span className="text-xs text-slate-400">--</span>
            )}
            {items.map((item) => {
                const label = item.name || sectorName(item.ticker);
                return (
                    <span
                        key={item.ticker}
                        className={cn(
                            'inline-flex items-baseline gap-1.5 text-xs font-bold px-2.5 py-1 rounded-md border max-w-full',
                            tone === 'positive'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                                : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800',
                        )}
                    >
                        <span className="font-mono">{item.ticker}</span>
                        {label && (
                            <span className="font-medium opacity-80 truncate">
                                {label}
                            </span>
                        )}
                    </span>
                );
            })}
        </div>
    </div>
);

export default MacroContextBanner;
